import axios from "axios";
import { IDENTITY_AUTH_PROXY } from "../constants";
import { isNative, NativeStorage } from "../util/platform";
import { LiveApiCache } from "./LiveApiClient";

class IdentityApiCache {
	/** @type Promise */
	static SharedTokenFetch
}

const BASE_URL = isNative()
	? process.env.REACT_APP_IDENTITY_SERVER_BASE_URL
	: window.location.origin + "/identity";

if (isNative() && !process.env.REACT_APP_IDENTITY_SERVER_BASE_URL) {
	console.error('REACT_APP_IDENTITY_SERVER_BASE_URL is required for native builds');
}
if (isNative() && !process.env.REACT_APP_IDENTITY_SERVER_TOKEN) {
	console.error('REACT_APP_IDENTITY_SERVER_TOKEN is required for native builds');
}

// On native there's no identity proxy server, so we authenticate directly
// with the identity server using a build-time token baked into the app bundle.
const nativeHeaders = isNative() ? {
	Authorization: process.env.REACT_APP_IDENTITY_SERVER_TOKEN,
	'keychain-profile': '3',
	'keychain': '1',
} : {};

const IdentityClient = axios.create({
	baseURL: BASE_URL,
	headers: {
		"Content-Type": "application/x-www-form-urlencoded",
		Accept: "application/json",
		...nativeHeaders,
	},
});

IdentityClient.interceptors.request.use(async config => {
	const isGetTokenRequest = config.url === "getToken" || config.url === "/getToken";

	if (isGetTokenRequest) {
		// On native, getToken reads from Capacitor Preferences instead of the PHP proxy
		if (isNative()) {
			try {
				const token = await NativeStorage.getToken();
				// Return a mock response structure so callers get { data: { token } }
				config.adapter = () => Promise.resolve({
					data: { token },
					status: 200,
					statusText: 'OK',
					headers: {},
					config,
				});
			} catch (err) {
				console.error('Failed to read token from NativeStorage:', err);
				config.adapter = () => Promise.reject(err);
			}
			return config;
		}
		// For web getToken requests, just pass through - no auth needed
		return config;
	}

	// For other requests, fetch a token first
	if (isNative()) {
		const token = await NativeStorage.getToken();
		if (token && config.data) {
			config.data.auth = token;
		}
		return config;
	}

	IdentityApiCache.SharedTokenFetch ??= fetch(`${BASE_URL}/getToken`, {
		method: "POST",
	}).then(r => r.json());
	
	// if request is ever rejected (too many requests maybe) it will block future ones; catch and clear
	try {
		const { token } = await IdentityApiCache.SharedTokenFetch;
		if (config.data) {
			const auth_to_use = token && token !== "undefined" ? token : config.data["auth-token"] ?? LiveApiCache.Get();
			if (auth_to_use) {
				config.data.auth = auth_to_use;
			}
		}
	} catch (e) {
		console.error("Failed to fetch token:", e);
	}
	
	IdentityApiCache.SharedTokenFetch = undefined;

	return config;
});

export default IdentityClient;
